import { PRINT_ATTRIBUTION_PARAM, readPrintAttributionToken } from "@favecard/contracts/utils/print-attribution";
import { JOIN_SOURCE_PARAM, readJoinSourceCode } from "@favecard/contracts/utils/join-source";

/** Preserve the print origin and a link's source code across the existing legacy → Business Page move. */
export function legacyRedirectUrl(baseUrl, slug, shortCode, search) {
  const target = new URL(`${baseUrl.replace(/\/$/, "")}/${encodeURIComponent(slug)}/${encodeURIComponent(shortCode)}`);
  const params = new URLSearchParams(search);
  for (const key of ["email", "firstName"]) {
    const value = params.get(key);
    if (value) target.searchParams.set(key, value);
  }
  if (params.get("demo") === "true") target.searchParams.set("demo", "true");
  const printToken = readPrintAttributionToken(search);
  if (printToken) target.searchParams.set(PRINT_ATTRIBUTION_PARAM, printToken);
  // docs/product/JOIN-SOURCE.md: a QR's `via`, only a valid one, never on a demo.
  const joinCode = readJoinSourceCode(search);
  if (joinCode) target.searchParams.set(JOIN_SOURCE_PARAM, joinCode);
  return target.toString();
}

/** Where cards live now; unset only in a local build without a Business Page. */
export function businessPageBaseUrl() {
  return import.meta.env.VITE_BUSINESS_PAGE_BASE_URL;
}

/** Leave this form for the card on the Business Page, replacing this history entry so Back does not return here. */
export function openOnBusinessPage(url) {
  window.location.replace(url);
}

/** The by-ids lookup's two tries: a cold start can take longer than the first. */
export const CARD_LOOKUP_TIMEOUTS_MS = [5000, 15000];

/**
 * Which card an old ids link names, as its Business Page slug and short code.
 * - `found`: open it there;
 * - `missing`: the lookup answered 404, or the card has no Business Page address: the link no longer opens a card;
 * - `unreachable`: no answer after both tries (timeout, network or server error): the person may try again.
 */
export async function lookUpCardByIds(url, timeoutsMs = CARD_LOOKUP_TIMEOUTS_MS) {
  for (const timeoutMs of timeoutsMs) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetch(url, { signal: controller.signal });
      if (response.status === 404) return { kind: "missing" };
      if (response.ok) {
        const data = await response.json();
        return data?.slug && data?.shortCode
          ? { kind: "found", slug: data.slug, shortCode: data.shortCode }
          : { kind: "missing" };
      }
    } catch {
      // Timed out or no network: the next try, if any.
    } finally {
      clearTimeout(timer);
    }
  }
  return { kind: "unreachable" };
}
